import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";

import {
  createConversation,
  createLookupInternalKnowledgeEventSource,
  requestLookupInternalKnowledge,
  BrowserPlan,
  EventSourceLike,
} from "../api-services/ai-chat";
import { extractBrowserPlansFromContent } from "../utils/browser-plans";
import { PolicySource, LookupStep } from "../type/chat";
import { QUERY_KEY } from "../commons/query-key";

type Attachment = {
  url?: string;
  name?: string;
  type?: string;
};

export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  files?: Attachment[];
  isStreaming?: boolean;
  isErrored?: boolean;
  browserPlans?: BrowserPlan[];
  policySources?: PolicySource[];
};

type DraftTaskMap = Record<string, { placeholderIndex: number }>;

const createId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const useBackofficeChat = (
  tenantId?: string | null,
  isOpen?: boolean,
  agencyId?: string
) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draftTasks, setDraftTasks] = useState<DraftTaskMap>({});
  const [inputValue, setInputValue] = useState("");
  const [files, setFiles] = useState<Attachment[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStep, setCurrentStep] = useState<LookupStep | null>(null);
  const [conversationID, setConversationID] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSourceLike | null>(null);
  const messagesRef = useRef<ChatMessage[]>(messages);
  const draftTasksRef = useRef<DraftTaskMap>(draftTasks);
  const conversationIDRef = useRef<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    draftTasksRef.current = draftTasks;
  }, [draftTasks]);

  useEffect(() => {
    conversationIDRef.current = conversationID;
  }, [conversationID]);

  const handleSendMessage = useCallback(
    async (content?: string, attachments: Attachment[] = []) => {
      if (!tenantId) return;
      const trimmed = (content || "").trim();
      if (!trimmed) return;

      const historyForApi = messagesRef.current
        .filter((m) => {
          // Filter out messages with browserPlans (similar to copilot)
          if (
            m.role === "assistant" &&
            m.browserPlans &&
            m.browserPlans.length > 0
          ) {
            return false;
          }
          return m.role === "user" || m.role === "assistant";
        })
        .map((m) => ({ role: m.role, content: m.content }));

      let placeholderIndex: number | null = null;
      setMessages((prev) => {
        const userMessage: ChatMessage = {
          id: createId(),
          role: "user",
          content: trimmed,
          files: attachments,
        };
        const assistantPlaceholder: ChatMessage = {
          id: createId(),
          role: "assistant",
          content: "",
          isStreaming: true,
        };
        placeholderIndex = prev.length + 1;
        return [...prev, userMessage, assistantPlaceholder];
      });
      setIsStreaming(true);
      setInputValue("");
      setFiles([]);
      try {
        // Create conversation on first message
        let currentConversationID = conversationIDRef.current;
        if (!currentConversationID) {
          try {
            const convRes = await createConversation(agencyId);
            const convData = (convRes as any)?.data?.data ?? (convRes as any)?.data;
            currentConversationID = convData?.conversationID ?? null;
            if (currentConversationID) {
              setConversationID(currentConversationID);
            }
          } catch {
            // Non-blocking: continue without conversationID
          }
        }

        const historyForApiBlocks = historyForApi.map((m) => ({
          role: m.role,
          blocks: [{ block_type: "text" as const, text: m.content }],
        }));

        const res = await requestLookupInternalKnowledge(
          {
            chatHistory: [
              ...historyForApiBlocks,
              {
                role: "user",
                blocks: [{ block_type: "text", text: trimmed }],
              },
            ],
            currentPlatform: "web",
            ...(currentConversationID ? { conversationID: currentConversationID } : {}),
          },
          agencyId
        );

        const taskId = (res as any)?.data?.taskId || (res as any)?.taskId;
        if (taskId) {
          const idx = placeholderIndex;
          setDraftTasks((prev) => ({
            ...prev,
            [taskId]: { placeholderIndex: idx ?? prev.length },
          }));
          queryClient.invalidateQueries([QUERY_KEY.CONVERSATION_LIST, agencyId]);
        } else {
          setMessages((prev) => {
            const next = [...prev];
            if (placeholderIndex != null && next[placeholderIndex]) {
              next[placeholderIndex] = {
                ...next[placeholderIndex],
                content: "Unable to send message. Please try again.",
                isStreaming: false,
                isErrored: true,
              };
            }
            return next;
          });
          setIsStreaming(false);
        }
      } catch (error) {
        setMessages((prev) => {
          const next = [...prev];
          if (placeholderIndex != null && next[placeholderIndex]) {
            next[placeholderIndex] = {
              ...next[placeholderIndex],
              content: "Something went wrong. Please try again later.",
              isStreaming: false,
              isErrored: true,
            };
          }
          return next;
        });
        setIsStreaming(false);
      }
    },
    [tenantId, agencyId]
  );

  // Connect if chat is active OR (fallback) if we have pending tasks
  const shouldConnect = Object.keys(draftTasks).length > 0;

  useEffect(() => {
    if (!tenantId || !shouldConnect) {
      return undefined;
    }

    let isCancelled = false;

    const setupEventSource = async () => {
      try {
        const source = await createLookupInternalKnowledgeEventSource(tenantId);
        if (isCancelled) {
          source.close();
          return;
        }
        eventSourceRef.current = source;

        source.onmessage = (evt: MessageEvent<string>) => {
          try {
            const payload = JSON.parse(evt.data);
            const { taskId, status, step, result } = payload;
            if (!taskId) return;

            const task = draftTasksRef.current[taskId];
            // Only process messages belonging to sent task; ignore SSE for unrelated tasks
            if (!task) return;
            if (
              step &&
              typeof step === "object" &&
              "name" in step &&
              "displayKey" in step &&
              status !== "completed" &&
              status !== "failed"
            ) {
              setCurrentStep(step as LookupStep);
            }

            if (status === "completed" || status === "failed") {
              setCurrentStep(null);
            }

            setMessages((prev) => {
              const next = [...prev];
              const hasPlaceholder =
                task &&
                task.placeholderIndex != null &&
                next[task.placeholderIndex];

              if (status === "completed" && result?.response) {
                const rawContent = typeof result.response === "string"
                  ? result.response
                  : Array.isArray(result.response)
                    ? result.response
                      .sort((a: any, b: any) => a.order - b.order)
                      .map((chunk: any) => chunk.content)
                      .join("\n\n")
                    : "";

                const { cleanedContent, plans } =
                  extractBrowserPlansFromContent(rawContent);

                // Extract policy sources from result
                const policySources: PolicySource[] = result?.policy_sources || [];

                const files: Attachment[] = [];

                if (hasPlaceholder) {
                  next[task.placeholderIndex] = {
                    ...next[task.placeholderIndex],
                    // If plans exist, hide text content (only render plans)
                    content: plans.length > 0 ? "" : cleanedContent,
                    files,
                    isStreaming: false,
                    isErrored: false,
                    browserPlans: plans,
                    policySources: policySources.length > 0 ? policySources : undefined,
                  };
                } else {
                  next.push({
                    id: createId(),
                    role: "assistant",
                    content: plans.length > 0 ? "" : cleanedContent,
                    files,
                    isStreaming: false,
                    browserPlans: plans,
                    policySources: policySources.length > 0 ? policySources : undefined,
                  });
                }
              } else if (status === "failed") {
                if (hasPlaceholder) {
                  next[task.placeholderIndex] = {
                    ...next[task.placeholderIndex],
                    content: "Something went wrong. Please try again later.",
                    isStreaming: false,
                    isErrored: true,
                  };
                } else {
                  next.push({
                    id: createId(),
                    role: "assistant",
                    content: "Something went wrong. Please try again later.",
                    isStreaming: false,
                    isErrored: true,
                  });
                }
              }
              return next;
            });

            if (status === "completed" || status === "failed") {
              setDraftTasks((prev) => {
                const next = { ...prev };
                delete next[taskId];
                setIsStreaming(Object.keys(next).length > 0);
                return next;
              });
            }
          } catch (err) {
            // swallow SSE parse errors
          }
        };

        source.onerror = () => {
          source.close();
          eventSourceRef.current = null;
        };
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to establish EventSource connection:", err);
        setMessages((prev) => [
          ...prev,
          {
            id: createId(),
            role: "assistant",
            content:
              "Connection to server failed. Real-time updates may be unavailable. Please refresh the page.",
            isErrored: true,
          },
        ]);
      }
    };

    setupEventSource();

    return () => {
      isCancelled = true;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [tenantId, shouldConnect]);

  const resetChat = useCallback(() => {
    setMessages([]);
    setDraftTasks({});
    setInputValue("");
    setFiles([]);
    setIsStreaming(false);
    setCurrentStep(null);
    setConversationID(null);
  }, []);

  const loadConversation = useCallback(
    (id: string, loadedMessages: ChatMessage[]) => {
      setConversationID(id);
      setMessages(loadedMessages);
      setDraftTasks({});
      setInputValue("");
      setFiles([]);
      setIsStreaming(false);
    },
    []
  );

  return {
    messages,
    setMessages,
    draftTasks,
    isStreaming,
    currentStep,
    inputValue,
    files,
    conversationID,
    setInputValue,
    setFiles,
    handleSendMessage,
    resetChat,
    loadConversation,
  };
};
export default useBackofficeChat;
